/// <amd-module name="Tfs/Platform/NavigationSettings" />

import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";

/**
 * Service for interacting with a users settings regarding to navigation.
 */
export interface ITfsNavigationSettingsService extends IVssService {
    /**
     * Sets the specified settings for the current user.
     *
     * @param name Name of setting to store
     * @param value Value of setting to store
     */
    setUserSetting<T>(name: string, value: T): void;

    /**
     * Attempts to look up the specified setting.  If the setting is not found and a default value is
     * supplied, that will be returned.
     *
     * @param name Name of setting to lookup
     * @param defaultValue Default value to use if setting is not found
     */
    getUserSetting<T>(name: string, defaultValue?: T): T | undefined;
}

const DATA_PROVIDER_ID = "ms.vss-tfs-web.navigation-settings-data-provider";
const PREFIX = "Navigation/";
const USER_KEY = "user";

/**
 * Implementation of the settings service
 */
class TfsNavigationSettingsService extends VssService implements ITfsNavigationSettingsService {
    private _prefs: { [key: string]: any };

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        this._prefs = contributionService.getData(DATA_PROVIDER_ID) || {};

        if (!this._prefs[USER_KEY]) {
            this._prefs[USER_KEY] = {} as { [key: string]: any };
        }
    }

    public setUserSetting(name: string, value: any): Promise<void> {
        // Save preference on the page
        this._prefs[USER_KEY][name] = value;

        // Save preference on the server
        const settingsToUpdate: any = {};
        settingsToUpdate[PREFIX + name] = value;

        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        return settingsService.setEntries(settingsToUpdate, SettingsUserScope.Me);
    }

    public getUserSetting<T>(name: string, defaultValue?: T): T | undefined {
        const value = this._prefs[USER_KEY][name];
        if (value !== undefined && value !== null) {
            return value;
        } else {
            return defaultValue;
        }
    }
}

Services.add("ITfsNavigationSettingsService", { serviceFactory: TfsNavigationSettingsService });
